import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import ResponsiveDropdown from 'components/ResponsiveDropdown';
import EyeIcon from 'icons/Eye.icon';
import EyeoffIcon from 'icons/Eyeoff.icon';
import AdjustmentsIcon from 'icons/Adjustments.icon';
import { RequiredVisibleInfo } from 'models/Listing';
import RequiredVisibleForm from './components/RequiredVisibleForm';
import useCallbackRef from 'hooks/useCallbackRef';
import { ResponsiveDropdownRef } from 'components/ResponsiveDropdown/ResponsiveDropdown.component';

import './FieldRequiredVisibleInfo.styles.scss';

export type FieldRequiredVisibleInfoProps = {
  className?: string;
  isDefaultRequired: boolean;
  canChangeRequired: boolean;
  fieldLabel: string;
  fieldValue: RequiredVisibleInfo;
  onSubmit: (
    values: RequiredVisibleInfo,
    onSuccess: () => void,
  ) => Promise<void>;
};

const FieldRequiredVisibleInfo: React.FC<FieldRequiredVisibleInfoProps> = (
  props,
) => {
  const {
    className,
    fieldLabel,
    fieldValue,
    canChangeRequired,
    isDefaultRequired,
    onSubmit,
  } = props;

  const [responsiveDropdown, responsiveDropdownRef] =
    useCallbackRef<ResponsiveDropdownRef>();

  const values = fieldValue || {
    isRequired: isDefaultRequired,
    isVisible: true,
  };

  const { isRequired, isVisible } = values;

  const classes = classNames(
    'field-required-visible-info',
    { red: isRequired },
    className,
  );

  const { t } = useTranslation();

  const trigger = useMemo(
    () => (
      <Button variant="outline" className={classes}>
        {isVisible ? <EyeIcon /> : <EyeoffIcon />}
        {t(isRequired ? 'required' : 'optional_UC')}
        <AdjustmentsIcon className="adjustment-icon" />
      </Button>
    ),
    [classes, isRequired, isVisible, t],
  );

  return (
    <ResponsiveDropdown
      ref={responsiveDropdownRef}
      trigger={trigger}
      modalProps={{ title: fieldLabel }}
    >
      <RequiredVisibleForm
        canChangeRequired={canChangeRequired}
        initialValues={values}
        onCancel={responsiveDropdown?.close}
        onSubmit={(values) => onSubmit(values, responsiveDropdown?.close)}
      />
    </ResponsiveDropdown>
  );
};

export default FieldRequiredVisibleInfo;
